// Account /Orders panel — PROMPT 2.7-B §5.
// Lists the signed-in customer's Shopify orders and exposes a
// "REQUEST CANCELLATION" button per order (opt-in, per PROMPT rules).

import { useCallback, useEffect, useState } from 'react';
import { ACCOUNT } from '../../constants/testIds';
import { API_BASE } from '../../lib/apiBase';

// Cookie-bearing auth calls MUST be same-origin (window.location.origin) so
// the oat_* cookies set on this host are sent. The baked REACT_APP_BACKEND_URL
// can point at the old preview host on production — never use it here.
const BACKEND = API_BASE;

function formatINR(amount) {
        if (amount == null) return '—';
        const n = Number(amount);
        if (Number.isNaN(n)) return String(amount);
        return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatDate(iso) {
        if (!iso) return '';
        try {
                const d = new Date(iso);
                return d.toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                });
        } catch (_e) {
                return iso;
        }
}

function OrderRow({ order, onCancelClick, cancellingId }) {
        const total = (order.totalPrice && order.totalPrice.amount) || null;
        const dispatched = order.already_dispatched;
        const cancelReq = order.cancel_requested;
        const cancellable = order.cancellable;
        const isCancelling = cancellingId === order.id;

        return (
                <div
                        data-testid={ACCOUNT.orderRow}
                        className="border border-white/15 p-5 sm:p-6"
                >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div>
                                        <p
                                                data-testid={ACCOUNT.orderRowName}
                                                className="font-display text-[20px] uppercase leading-none"
                                        >
                                                {order.name || '—'}
                                        </p>
                                        <p className="mt-1 font-mono text-[11px] tracking-[0.14em] text-white/45 uppercase">
                                                {formatDate(order.processedAt)}
                                        </p>
                                </div>
                                <div className="text-left sm:text-right">
                                        <p
                                                data-testid={ACCOUNT.orderRowTotal}
                                                className="font-display text-[18px]"
                                        >
                                                {formatINR(total)}
                                        </p>
                                        <p
                                                data-testid={ACCOUNT.orderRowStatus}
                                                className="mt-1 font-mono text-[11px] tracking-[0.14em] uppercase text-white/60"
                                        >
                                                {order.displayFinancialStatus || '—'}
                                                {order.displayFulfillmentStatus
                                                        ? ` · ${order.displayFulfillmentStatus}`
                                                        : ''}
                                        </p>
                                </div>
                        </div>

                        {order.lineItems && order.lineItems.length > 0 && (
                                <ul className="mt-4 space-y-1">
                                        {order.lineItems.slice(0, 4).map((li) => (
                                                <li
                                                        key={li.id}
                                                        className="font-sans text-[13px] text-white/70"
                                                >
                                                        {li.quantity}× {li.title || (li.variant && li.variant.title) || 'Item'}
                                                </li>
                                        ))}
                                        {order.lineItems.length > 4 && (
                                                <li className="font-mono text-[11px] text-white/40 uppercase">
                                                        +{order.lineItems.length - 4} more
                                                </li>
                                        )}
                                </ul>
                        )}

                        <div className="mt-5">
                                {cancellable && (
                                        <button
                                                type="button"
                                                data-testid={ACCOUNT.cancelRequestButton}
                                                onClick={() => onCancelClick(order)}
                                                disabled={isCancelling}
                                                className="inline-block border border-white/25 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                                {isCancelling ? 'Submitting…' : 'Request Cancellation'}
                                        </button>
                                )}
                                {cancelReq && (
                                        <p
                                                data-testid={ACCOUNT.cancelAlreadyRequestedLabel}
                                                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FF9F1C]"
                                        >
                                                Cancellation requested — pending ops review
                                        </p>
                                )}
                                {dispatched && !cancelReq && (
                                        <p
                                                data-testid={ACCOUNT.cancelDispatchedLabel}
                                                className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/50"
                                        >
                                                Already dispatched — contact ops via WhatsApp +91 83682 49423
                                        </p>
                                )}
                        </div>
                </div>
        );
}

function ConfirmModal({ order, onConfirm, onCancel, submitting }) {
        if (!order) return null;
        return (
                <div
                        data-testid={ACCOUNT.cancelConfirmModal}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
                >
                        <div className="max-w-md w-full border border-white/20 bg-[#0A0A0A] p-6">
                                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FF9F1C]">
                                        Confirm cancellation request
                                </p>
                                <h3 className="mt-3 font-display text-[22px] uppercase leading-tight">
                                        Cancel {order.name}?
                                </h3>
                                <p className="mt-3 font-sans text-[14px] text-white/70">
                                        Your request will be logged and our ops team will review within
                                        1 business day. If the order has already been dispatched, you'll
                                        need to contact ops directly.
                                </p>
                                <div className="mt-6 flex gap-3">
                                        <button
                                                type="button"
                                                data-testid={ACCOUNT.cancelConfirmYes}
                                                disabled={submitting}
                                                onClick={onConfirm}
                                                className="flex-1 border border-[#FF9F1C] text-[#FF9F1C] font-mono text-[12px] tracking-[0.2em] uppercase px-4 py-3 hover:bg-[#FF9F1C] hover:text-black transition-colors disabled:opacity-50"
                                        >
                                                {submitting ? 'Submitting…' : 'Yes, request cancel'}
                                        </button>
                                        <button
                                                type="button"
                                                data-testid={ACCOUNT.cancelConfirmNo}
                                                onClick={onCancel}
                                                disabled={submitting}
                                                className="flex-1 border border-white/25 font-mono text-[12px] tracking-[0.2em] uppercase px-4 py-3 hover:border-white transition-colors disabled:opacity-50"
                                        >
                                                Keep order
                                        </button>
                                </div>
                        </div>
                </div>
        );
}

export default function OrdersPanel({ id }) {
        const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
        const [orders, setOrders] = useState([]);
        const [confirmOrder, setConfirmOrder] = useState(null);
        const [submittingId, setSubmittingId] = useState(null);
        const [toast, setToast] = useState('');

        const load = useCallback(async () => {
                setStatus('loading');
                try {
                        const r = await fetch(`${BACKEND}/api/account/orders`, {
                                credentials: 'include',
                        });
                        if (!r.ok) {
                                setStatus('ready');
                                setOrders([]);
                                return;
                        }
                        const data = await r.json();
                        setOrders(Array.isArray(data.orders) ? data.orders : []);
                        setStatus('ready');
                } catch (_e) {
                        setStatus('ready');
                        setOrders([]);
                }
        }, []);

        useEffect(() => {
                load();
        }, [load]);

        const handleCancelClick = (order) => setConfirmOrder(order);

        const handleConfirmCancel = async () => {
                if (!confirmOrder) return;
                setSubmittingId(confirmOrder.id);
                try {
                        // The order id from Shopify is a GID: `gid://shopify/Order/<num>`.
                        // We pass it verbatim as a path segment — the FastAPI route
                        // uses a `:path` converter so slashes are preserved.
                        const idPath = encodeURI(confirmOrder.id);
                        const r = await fetch(
                                `${BACKEND}/api/account/orders/${idPath}/request-cancellation`,
                                { method: 'POST', credentials: 'include' }
                        );
                        const data = await r.json().catch(() => ({}));
                        if (r.ok && data && data.ok) {
                                setToast(
                                        data.action === 'already_submitted'
                                                ? 'Cancellation already requested for this order.'
                                                : 'Cancellation request logged. Ops will review shortly.'
                                );
                                setConfirmOrder(null);
                                await load();
                        } else if (r.status === 409) {
                                setToast('This order has already been dispatched. Contact ops via WhatsApp.');
                                setConfirmOrder(null);
                                await load();
                        } else {
                                setToast('Could not submit. Please try again.');
                        }
                } catch (_e) {
                        setToast('Network error. Please try again.');
                } finally {
                        setSubmittingId(null);
                        setTimeout(() => setToast(''), 6000);
                }
        };

        return (
                <section
                        id={id}
                        data-testid={ACCOUNT.ordersPanel}
                        className="mt-12"
                >
                        <p className="font-mono text-[11px] tracking-[0.18em] text-white/50 uppercase">
                                Your Orders
                        </p>
                        <h2 className="mt-2 font-display text-[26px] sm:text-[32px] uppercase leading-none">
                                Order History
                        </h2>

                        {status === 'loading' && (
                                <p
                                        data-testid={ACCOUNT.ordersLoading}
                                        className="mt-6 font-mono text-[12px] tracking-[0.2em] text-white/45 uppercase"
                                >
                                        Loading orders…
                                </p>
                        )}

                        {status === 'ready' && orders.length === 0 && (
                                <p
                                        data-testid={ACCOUNT.ordersEmpty}
                                        className="mt-6 font-mono text-[12px] tracking-[0.2em] text-white/45 uppercase"
                                >
                                        No orders yet.
                                </p>
                        )}

                        {status === 'error' && (
                                <p className="mt-6 font-mono text-[12px] tracking-[0.2em] text-white/45 uppercase">
                                        Could not load orders.
                                </p>
                        )}

                        {status === 'ready' && orders.length > 0 && (
                                <div
                                        data-testid={ACCOUNT.ordersList}
                                        className="mt-6 grid grid-cols-1 gap-4"
                                >
                                        {orders.map((o) => (
                                                <OrderRow
                                                        key={o.id}
                                                        order={o}
                                                        onCancelClick={handleCancelClick}
                                                        cancellingId={submittingId}
                                                />
                                        ))}
                                </div>
                        )}

                        <ConfirmModal
                                order={confirmOrder}
                                submitting={submittingId != null}
                                onConfirm={handleConfirmCancel}
                                onCancel={() => setConfirmOrder(null)}
                        />

                        {toast && (
                                <div
                                        data-testid={ACCOUNT.cancelSuccessToast}
                                        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 border border-[#FF9F1C] bg-[#0A0A0A] px-5 py-3 font-mono text-[12px] tracking-[0.14em] uppercase text-[#FF9F1C]"
                                >
                                        {toast}
                                </div>
                        )}
                </section>
        );
}
